import { createSignal, For, type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import {
  type AttentionRow,
  attentionBox,
  attentionHint,
  attentionHintText,
  attentionList,
  type IssueHealth,
} from '../lib/fleet-attention';
import { shortId } from '../lib/fleet-machines';
import type { UtilBox, UtilKeyRoom } from '../lib/fleetUtilization';
import { decimal } from '../lib/time';
import { Panel } from './Panel';
import { Button } from './ui/Button';

// Needs attention (#6559, prototype `attention()`): at most three rows, then "+N more"
// with a line naming what is behind it. Presenter over `attentionList`
// (`lib/fleet-attention.ts`), which owns the order. Renders nothing when nothing needs
// attention: the tile above already says "All clear".
//
// THE DISPATCH ROW (#6708) says key slots stand free beside waiting work. It names no
// machine and no task or slot count (nothing persists them), and offers no action: the ops
// alert is already raised, which the row says.
//
// THE INSIGHT ROW names a server whose machines are overloaded together; its "View server"
// turns the Machines list's grouping on and scrolls to it (#6560). It is a view change, not
// a write, so every role gets it.
//
// "View machine" opens the machine's details panel (#6563) through the page's one opener,
// on every machine row for every viewer (owner ruling 2026-10-22). It is a read: the panel
// shows a member, a viewer or a machine from another org with no write (G21, G23).
//
// The copy states what the verdict knows and no more: the check-in edge it crossed (over
// 90 seconds, over 30 minutes; owner ruling G14), never a "last seen" time this read does
// not carry.
//
// THE STUCK JOB ROW (#6746) restates the warden's 60-minute and the reaper's 180-minute
// rules in the page's words, a listed deviation (the prototype has no stuck state,
// `docs/design/fleet-redesign/brief.md`). It names the machine by name plus its 8-character
// id, never the hostname, and its only action is the shared "View machine". The verdict
// carries no silence time, so the row shows none: the hour is the rule, not a reading.

type Tone = 'crit' | 'warn' | 'info';

const ISSUE_TONE: Readonly<Record<IssueHealth, Tone>> = {
  offline: 'crit',
  not_responding: 'crit',
  overloaded: 'crit',
  disk_critical: 'crit',
  memory_critical: 'crit',
  disk_warning: 'warn',
  memory_warning: 'warn',
};

const ISSUE_LABEL: Readonly<Record<IssueHealth, string>> = {
  offline: 'fleet.top.attention.label_offline',
  not_responding: 'fleet.top.attention.label_not_responding',
  overloaded: 'fleet.top.attention.label_overloaded',
  disk_critical: 'fleet.top.attention.label_disk_critical',
  memory_critical: 'fleet.top.attention.label_memory_critical',
  disk_warning: 'fleet.top.attention.label_disk_warning',
  memory_warning: 'fleet.top.attention.label_memory_warning',
};

const GB = 1e9;
const gb = (bytes: number): string => decimal(bytes / GB);

function overloadedMeans(box: UtilBox): string {
  const ratio = box.verdict.signals.cpu.ratio;
  return ratio === null
    ? t('fleet.top.attention.means_overloaded_plain')
    : t('fleet.top.attention.means_overloaded', { ratio: decimal(ratio, 2) });
}

function diskMeans(box: UtilBox): string {
  const free = box.stats?.workspaceDisk?.freeBytes ?? null;
  return free === null
    ? t('fleet.top.attention.means_disk_plain')
    : t('fleet.top.attention.means_disk', { free: gb(free) });
}

function memoryMeans(box: UtilBox): string {
  const total = box.specs?.mem_bytes ?? null;
  const free = box.stats?.freeMemBytes ?? null;
  return total === null || free === null
    ? t('fleet.top.attention.means_memory_plain')
    : t('fleet.top.attention.means_memory', { used: gb(total - free), total: gb(total) });
}

/** What a row says: its chip, its sentence pair, and the machine it opens (if any). */
function rowText(row: AttentionRow): { tone: Tone; label: string; what: string; means: string } {
  if (row.kind === 'insight') {
    const vars = {
      count: row.insight.overloaded,
      total: row.insight.total,
      server: row.insight.serverName,
    };
    return {
      tone: 'info',
      label: t('fleet.top.attention.label_insight'),
      what: t('fleet.top.attention.what_insight', vars),
      means: t('fleet.top.attention.means_insight', vars),
    };
  }
  if (row.kind === 'dispatch') {
    return {
      tone: 'warn',
      label: t('fleet.top.attention.label_dispatch'),
      what: tPlural('fleet.top.attention.what_dispatch', row.keys, { count: row.keys }),
      means: t('fleet.top.attention.means_dispatch'),
    };
  }
  const name = row.box.display_name;
  if (row.kind === 'stuck_job') {
    return {
      tone: 'warn',
      label: t('fleet.top.attention.label_stuck_job'),
      what: t('fleet.top.attention.what_stuck_job', {
        name: `${name} · ${shortId(row.box.id)}`,
      }),
      means: t('fleet.top.attention.means_stuck_job'),
    };
  }
  if (row.kind === 'cleanup') {
    return {
      tone: 'warn',
      label: t('fleet.top.attention.label_cleanup'),
      what: t('fleet.top.attention.what_cleanup', { name }),
      means: tPlural('fleet.top.attention.means_cleanup', row.errors, { count: row.errors }),
    };
  }
  const label = t(ISSUE_LABEL[row.health]);
  const tone = ISSUE_TONE[row.health];
  switch (row.health) {
    case 'overloaded':
      return {
        tone,
        label,
        what: t('fleet.top.attention.what_overloaded', { name }),
        means: overloadedMeans(row.box),
      };
    case 'not_responding':
      return {
        tone,
        label,
        what: t('fleet.top.attention.what_not_responding', { name }),
        means: t('fleet.top.attention.means_not_responding'),
      };
    case 'offline':
      return {
        tone,
        label,
        what: t('fleet.top.attention.what_offline', { name }),
        means: t('fleet.top.attention.means_offline'),
      };
    case 'disk_critical':
    case 'disk_warning':
      return {
        tone,
        label,
        what: t('fleet.top.attention.what_disk', { name }),
        means: diskMeans(row.box),
      };
    case 'memory_critical':
    case 'memory_warning':
      return {
        tone,
        label,
        what: t('fleet.top.attention.what_memory', { name }),
        means: memoryMeans(row.box),
      };
  }
}

export function FleetAttention(props: {
  readonly boxes: readonly UtilBox[];
  /** The api's `key_room` (#6708): faulting adds the dispatch row. Absent: an older api. */
  readonly keyRoom?: UtilKeyRoom | null;
  /** Open the machine's details panel. */
  readonly onView: (box: UtilBox) => void;
  /** "View server": group the Machines list by server and bring it into view. */
  readonly onViewServer: () => void;
}): JSX.Element {
  const [expanded, setExpanded] = createSignal(false);
  const list = () =>
    attentionList(props.boxes, { showInsight: true, keyRoom: props.keyRoom ?? null });
  const rows = (): readonly AttentionRow[] =>
    expanded() ? [...list().shown, ...list().hidden] : list().shown;
  const count = (): string =>
    tPlural('fleet.top.tiles.machines', list().machines, { count: list().machines });
  // Every role opens the panel (non-admins read-only); the insight and dispatch rows name no box.
  const viewable = attentionBox;

  return (
    <Show when={list().shown.length > 0}>
      <Panel
        class="fleet-attn"
        title={t('fleet.top.attention.heading')}
        action={
          // The dispatch row alone flags no machine: no "0 machines" beside it.
          <Show when={list().machines > 0}>
            <span class="fleet-attn__count">{count()}</span>
          </Show>
        }
      >
        <ul class="fleet-attn__list">
          <For each={rows()}>
            {(row) => {
              const text = rowText(row);
              return (
                <li class="fleet-attn__row">
                  <span class={`fleet-verdict fleet-verdict--${text.tone}`}>{text.label}</span>
                  <p class="fleet-attn__text">
                    <span class="fleet-attn__what">{text.what}</span>{' '}
                    <span class="fleet-attn__means">{text.means}</span>
                  </p>
                  <Show when={viewable(row)}>
                    {(box) => (
                      <Button variant="outline" size="sm" onClick={() => props.onView(box())}>
                        {t('fleet.top.attention.view_machine')}
                      </Button>
                    )}
                  </Show>
                  <Show when={row.kind === 'insight'}>
                    <Button variant="outline" size="sm" onClick={() => props.onViewServer()}>
                      {t('fleet.top.attention.view_server')}
                    </Button>
                  </Show>
                </li>
              );
            }}
          </For>
        </ul>
        <Show when={list().hidden.length > 0}>
          <div class="fleet-attn__more">
            <Button
              variant="outline"
              size="sm"
              aria-expanded={expanded()}
              onClick={() => setExpanded((v) => !v)}
            >
              {expanded()
                ? t('fleet.top.attention.less')
                : t('fleet.top.attention.more', { count: list().hidden.length })}
            </Button>
            <Show when={!expanded()}>
              <span>{attentionHintText(attentionHint(list().hidden), t)}</span>
            </Show>
          </div>
        </Show>
      </Panel>
    </Show>
  );
}
